/**
 * Performance Monitoring Service
 * 
 * Tracks query performance, cache hit rates, and system metrics
 * for the Single Pain of Glass dashboard.
 */

import { log } from './LoggingService';

export interface PerformanceMetric {
  id: string;
  type: 'query' | 'cache' | 'api' | 'render';
  name: string;
  duration: number;
  timestamp: Date;
  metadata?: Record<string, any>;
}

export interface CacheMetrics {
  hits: number;
  misses: number;
  hitRate: number;
  totalRequests: number;
}

export interface QueryMetrics {
  totalQueries: number;
  averageDuration: number;
  slowestQuery: PerformanceMetric | null;
  fastestQuery: PerformanceMetric | null;
  errorRate: number;
}

export interface SystemMetrics {
  memoryUsage: number;
  cpuUsage: number;
  activeConnections: number;
  uptime: number;
}

class PerformanceMonitoringService {
  private static instance: PerformanceMonitoringService;
  private metrics: PerformanceMetric[] = [];
  private cacheStats: Map<string, CacheMetrics> = new Map();
  private queryStats: Map<string, QueryMetrics> = new Map();
  private maxMetrics = 1000; // Keep last 1000 metrics
  private isEnabled = true;

  private constructor() {
    this.startPeriodicCleanup();
  }

  static getInstance(): PerformanceMonitoringService {
    if (!PerformanceMonitoringService.instance) {
      PerformanceMonitoringService.instance = new PerformanceMonitoringService();
    }
    return PerformanceMonitoringService.instance;
  }

  /**
   * Track a performance metric
   */
  trackMetric(
    type: PerformanceMetric['type'],
    name: string,
    duration: number,
    metadata?: Record<string, any>
  ): void {
    if (!this.isEnabled) return;

    const metric: PerformanceMetric = {
      id: this.generateId(),
      type,
      name,
      duration,
      timestamp: new Date(),
      metadata
    };

    this.metrics.push(metric);
    this.trimMetrics();

    // Log performance metrics
    if (duration > 1000) {
      log.warn(`Slow ${type}: ${name} took ${duration}ms`, 'Performance', metadata);
    } else if (duration > 500) {
      log.info(`${type}: ${name} took ${duration}ms`, 'Performance', metadata);
    } else {
      log.debug(`${type}: ${name} took ${duration}ms`, 'Performance', metadata);
    }
  }

  /**
   * Track cache hit/miss
   */
  trackCacheAccess(cacheName: string, hit: boolean, duration?: number): void {
    if (!this.isEnabled) return;

    const stats = this.cacheStats.get(cacheName) || {
      hits: 0,
      misses: 0,
      hitRate: 0,
      totalRequests: 0
    };

    if (hit) {
      stats.hits++;
    } else {
      stats.misses++;
    }

    stats.totalRequests = stats.hits + stats.misses;
    stats.hitRate = stats.hits / stats.totalRequests;

    this.cacheStats.set(cacheName, stats);

    // Track as performance metric
    this.trackMetric('cache', `${cacheName}_${hit ? 'hit' : 'miss'}`, duration || 0, {
      cacheName,
      hit,
      hitRate: stats.hitRate
    });

    // Log cache performance
    if (stats.hitRate < 0.5 && stats.totalRequests > 10) {
      log.warn(`Low cache hit rate for ${cacheName}: ${(stats.hitRate * 100).toFixed(1)}%`, 'Cache');
    }
  }

  /**
   * Track API call performance
   */
  trackApiCall(
    endpoint: string,
    method: string,
    duration: number,
    statusCode: number,
    metadata?: Record<string, any>
  ): void {
    if (!this.isEnabled) return;

    this.trackMetric('api', `${method} ${endpoint}`, duration, {
      endpoint,
      method,
      statusCode,
      ...metadata
    });

    // Log API performance
    if (statusCode >= 400) {
      log.error(`API Error: ${method} ${endpoint} returned ${statusCode} in ${duration}ms`, 'API', metadata);
    } else if (duration > 2000) {
      log.warn(`Slow API: ${method} ${endpoint} took ${duration}ms`, 'API', metadata);
    }
  }

  /**
   * Track query performance
   */
  trackQuery(
    queryName: string,
    duration: number,
    resultCount?: number,
    metadata?: Record<string, any>
  ): void {
    if (!this.isEnabled) return;

    this.trackMetric('query', queryName, duration, {
      resultCount,
      ...metadata
    });

    // Update query stats
    const stats = this.queryStats.get(queryName) || {
      totalQueries: 0,
      averageDuration: 0,
      slowestQuery: null,
      fastestQuery: null,
      errorRate: 0
    };

    stats.totalQueries++;
    stats.averageDuration = (stats.averageDuration * (stats.totalQueries - 1) + duration) / stats.totalQueries;

    if (!stats.slowestQuery || duration > stats.slowestQuery.duration) {
      stats.slowestQuery = {
        id: this.generateId(),
        type: 'query',
        name: queryName,
        duration,
        timestamp: new Date(),
        metadata
      };
    }

    if (!stats.fastestQuery || duration < stats.fastestQuery.duration) {
      stats.fastestQuery = {
        id: this.generateId(),
        type: 'query',
        name: queryName,
        duration,
        timestamp: new Date(),
        metadata
      };
    }

    this.queryStats.set(queryName, stats);
  }

  /**
   * Track render performance
   */
  trackRender(
    componentName: string,
    duration: number,
    dataPoints?: number,
    metadata?: Record<string, any>
  ): void {
    if (!this.isEnabled) return;

    this.trackMetric('render', componentName, duration, {
      dataPoints,
      ...metadata
    });

    // Log render performance
    if (duration > 1000) {
      log.warn(`Slow render: ${componentName} took ${duration}ms`, 'Render', metadata);
    }
  }

  /**
   * Get cache metrics for a specific cache
   */
  getCacheMetrics(cacheName: string): CacheMetrics | null {
    return this.cacheStats.get(cacheName) || null;
  }

  /**
   * Get all cache metrics
   */
  getAllCacheMetrics(): Map<string, CacheMetrics> {
    return new Map(this.cacheStats);
  }

  /**
   * Get query metrics for a specific query
   */
  getQueryMetrics(queryName: string): QueryMetrics | null {
    return this.queryStats.get(queryName) || null;
  }

  /**
   * Get all query metrics
   */
  getAllQueryMetrics(): Map<string, QueryMetrics> {
    return new Map(this.queryStats);
  }

  /**
   * Get recent performance metrics
   */
  getRecentMetrics(limit: number = 100): PerformanceMetric[] {
    return this.metrics.slice(-limit);
  }

  /**
   * Get metrics by type
   */
  getMetricsByType(type: PerformanceMetric['type'], limit: number = 100): PerformanceMetric[] {
    return this.metrics
      .filter(metric => metric.type === type)
      .slice(-limit);
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary(): {
    totalMetrics: number;
    averageDuration: number;
    slowestMetric: PerformanceMetric | null;
    cacheHitRates: Record<string, number>;
    queryCounts: Record<string, number>;
  } {
    const totalMetrics = this.metrics.length;
    const averageDuration = this.metrics.reduce((sum, metric) => sum + metric.duration, 0) / totalMetrics;
    const slowestMetric = this.metrics.reduce((slowest, metric) => 
      !slowest || metric.duration > slowest.duration ? metric : slowest, null as PerformanceMetric | null
    );

    const cacheHitRates: Record<string, number> = {};
    this.cacheStats.forEach((stats, name) => {
      cacheHitRates[name] = stats.hitRate;
    });

    const queryCounts: Record<string, number> = {};
    this.queryStats.forEach((stats, name) => {
      queryCounts[name] = stats.totalQueries;
    });

    return {
      totalMetrics,
      averageDuration,
      slowestMetric,
      cacheHitRates,
      queryCounts
    };
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics = [];
    this.cacheStats.clear();
    this.queryStats.clear();
    log.info('Performance metrics cleared', 'Performance');
  }

  /**
   * Enable/disable monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    log.info(`Performance monitoring ${enabled ? 'enabled' : 'disabled'}`, 'Performance');
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): string {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      metrics: this.metrics,
      cacheStats: Object.fromEntries(this.cacheStats),
      queryStats: Object.fromEntries(this.queryStats),
      summary: this.getPerformanceSummary()
    }, null, 2);
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private trimMetrics(): void {
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }
  }

  private startPeriodicCleanup(): void {
    // Clean up old metrics every 5 minutes
    setInterval(() => {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
      this.metrics = this.metrics.filter(metric => metric.timestamp > fiveMinutesAgo);
    }, 5 * 60 * 1000);
  }
}

// Export singleton instance
export const performanceMonitor = PerformanceMonitoringService.getInstance();

// Export convenience functions
export const trackMetric = (
  type: PerformanceMetric['type'],
  name: string,
  duration: number,
  metadata?: Record<string, any>
) => performanceMonitor.trackMetric(type, name, duration, metadata);

export const trackCacheAccess = (cacheName: string, hit: boolean, duration?: number) =>
  performanceMonitor.trackCacheAccess(cacheName, hit, duration);

export const trackApiCall = (
  endpoint: string,
  method: string,
  duration: number,
  statusCode: number,
  metadata?: Record<string, any>
) => performanceMonitor.trackApiCall(endpoint, method, duration, statusCode, metadata);

export const trackQuery = (
  queryName: string,
  duration: number,
  resultCount?: number,
  metadata?: Record<string, any>
) => performanceMonitor.trackQuery(queryName, duration, resultCount, metadata);

export const trackRender = (
  componentName: string,
  duration: number,
  dataPoints?: number,
  metadata?: Record<string, any>
) => performanceMonitor.trackRender(componentName, duration, dataPoints, metadata);

export default performanceMonitor;
